import { Component, Input, ElementRef, AfterViewInit, ViewChild, ApplicationRef } from '@angular/core';
import { fromEvent } from 'rxjs';
import { switchMap, takeUntil, pairwise, startWith, defaultIfEmpty } from 'rxjs/operators'
import { Signature } from 'src/app/classes';
import { BackEndService } from 'src/app/backend-service';
import { PopUpData, PopUpComponent } from '../pop-up/pop-up.component';
import { ActivatedRoute, Router } from '@angular/router';
import { MatIcon } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
  selector: 'app-manage-signature',
  templateUrl: './manage-signature.component.html',
  styleUrls: ['./manage-signature.component.css']
})
export class ManageSignatureComponent implements AfterViewInit {

  // a reference to the canvas element from our template
  @ViewChild('canvas', { static: true }) public canvas: ElementRef<HTMLCanvasElement>;
  @ViewChild('link') public link: ElementRef<HTMLLinkElement>;
  @ViewChild('clear', { static: true }) public clear: ElementRef<MatIcon>;

  // setting a width and height for the canvas
  @Input() public width = 600;
  @Input() public height = 400;

  currentMinX = null;
  currentMinY = null;
  currentMaxX = null;
  currentMaxY = null;


  drawable: boolean = true;
  resized: boolean = false;

  private cx: CanvasRenderingContext2D;

  constructor(
    private _backEndService: BackEndService,
    private _popUp: MatSnackBar,
    private _route: ActivatedRoute,
    private _router: Router,
    private _app: ApplicationRef
  ){
    
  }

  public ngAfterViewInit() {
    const canvasEl: HTMLCanvasElement = this.canvas.nativeElement;
    this.cx = canvasEl.getContext('2d');

    canvasEl.width = this.width;
    canvasEl.height = this.height;

    this.cx.lineWidth = 4;
    this.cx.lineCap = 'round';
    this.cx.strokeStyle = '#000';

    this.captureEvents(canvasEl);
  }

  private captureEvents(canvasEl: HTMLCanvasElement) {
    fromEvent(canvasEl, 'mousedown')
      .pipe(
        switchMap((e) => {
          return fromEvent(canvasEl, 'mousemove')
            .pipe(
              startWith(e),
              takeUntil(fromEvent(canvasEl, 'mouseup')),
              takeUntil(fromEvent(canvasEl, 'mouseleave')),
              pairwise()
            )
        })
      )
      .subscribe((res: [MouseEvent, MouseEvent]) => {
        const rect = canvasEl.getBoundingClientRect();
        const prevPos = {
          x: res[0].clientX - rect.left,
          y: res[0].clientY - rect.top
        };

        const currentPos = {
          x: res[1].clientX - rect.left,
          y: res[1].clientY - rect.top
        };
        this.drawOnCanvas(prevPos, currentPos);
      });

    fromEvent(canvasEl, 'touchstart')
      .pipe(
        switchMap((e) => {
          e.preventDefault();
          return fromEvent(canvasEl, 'touchmove')
            .pipe(
              startWith(e),
              takeUntil(fromEvent(canvasEl, 'touchend')),
              pairwise(),
              defaultIfEmpty([e, e])
            )
        })
      )
      .subscribe((res: [TouchEvent, TouchEvent]) => {
        const rect = canvasEl.getBoundingClientRect();
        const prevPos = {
          x: res[0].touches[0].clientX - rect.left,
          y: res[0].touches[0].clientY - rect.top
        };

        const currentPos = {
          x: res[1].touches[0].clientX - rect.left,
          y: res[1].touches[0].clientY - rect.top
        };

        // this method we'll implement soon to do the actual drawing
        this.drawOnCanvas(prevPos, currentPos);
      });
  }

  clearSignature() {
    this.resized = false;
    this.drawable = true;
    this.canvas.nativeElement.height = this.height;
    this.canvas.nativeElement.width = this.width;
    this.cx.clearRect(0, 0, this.width, this.height);
    this.cx.lineWidth = 4;
    this.cx.lineCap = 'round';
    this.cx.strokeStyle = '#000';
  }

  private drawOnCanvas(
    prevPos: { x: number, y: number },
    currentPos: { x: number, y: number }
  ) {
    // incase the context is not set
    if (!this.cx || !this.drawable) { return; }

    if (this.currentMinX == null || this.currentMinX > currentPos.x) this.currentMinX = currentPos.x;
    if (this.currentMinY == null || this.currentMinY > currentPos.y) this.currentMinY = currentPos.y;
    if (this.currentMaxX == null || this.currentMaxX < currentPos.x) this.currentMaxX = currentPos.x;
    if (this.currentMaxY == null || this.currentMaxY < currentPos.y) this.currentMaxY = currentPos.y;

    // start our drawing path
    this.cx.beginPath();

    // we're drawing lines so we need a previous position
    if (prevPos) {
      // sets the start point
      this.cx.moveTo(prevPos.x, prevPos.y); // from

      // draws a line from the start pos until the current position
      this.cx.lineTo(currentPos.x, currentPos.y);

      // strokes the current path with the styles we set earlier
      this.cx.stroke();
    }
  }

  resizeImage() {
    /*
    this.cx.strokeStyle = "#324ea2";
    this.cx.lineWidth = 1;
    this.cx.beginPath();
    this.cx.arc(this.currentMinX-3, this.currentMinY-3, 3, 0, 2 * Math.PI);
    this.cx.moveTo(this.currentMinX, this.currentMinY-3);
    this.cx.lineTo(this.currentMaxX, this.currentMinY-3);
    this.cx.stroke();
    this.cx.beginPath();
    this.cx.arc(this.currentMaxX+3, this.currentMinY-3, 3, 0, 2 * Math.PI);
    this.cx.moveTo(this.currentMaxX+3, this.currentMinY);
    this.cx.lineTo(this.currentMaxX+3, this.currentMaxY);
    this.cx.stroke();
    this.cx.beginPath();
    this.cx.arc(this.currentMaxX+3, this.currentMaxY+3, 3, 0, 2 * Math.PI);
    this.cx.moveTo(this.currentMaxX, this.currentMaxY+3);
    this.cx.lineTo(this.currentMinX, this.currentMaxY+3);
    this.cx.stroke();
    this.cx.beginPath();
    this.cx.arc(this.currentMinX-3, this.currentMaxY+3, 3, 0, 2 * Math.PI);
    this.cx.moveTo(this.currentMinX-3, this.currentMaxY);
    this.cx.lineTo(this.currentMinX-3, this.currentMinY);
    this.cx.stroke();
    */
    let imageData = this.cx.getImageData(this.currentMinX - 5, this.currentMinY - 5, (this.currentMaxX - this.currentMinX + 10), (this.currentMaxY - this.currentMinY + 10));
    this.clearSignature();
    this.drawable = false;
    this.resized = true;
    this.canvas.nativeElement.width = (this.currentMaxX - this.currentMinX + 10);
    this.canvas.nativeElement.height = (this.currentMaxY - this.currentMinY + 10);
    this.cx.putImageData(imageData, 0, 0);
    let xF = this.width / (this.currentMaxX - this.currentMinX + 10);
    let yF = this.height / (this.currentMaxY - this.currentMinY + 10);
    /*
    if(xF > yF){
      this.canvas.nativeElement.width = (this.currentMaxX - this.currentMinX + 10) * yF;
      this.canvas.nativeElement.height = (this.currentMaxY - this.currentMinY + 10) * yF;
    } else {
      this.canvas.nativeElement.width = (this.currentMaxX - this.currentMinX + 10) * xF;
      this.canvas.nativeElement.height = (this.currentMaxY - this.currentMinY + 10) * xF;
    }
    */
  }

  saveSignature() {
    /*
    this.link.nativeElement.setAttribute('download', 'signature.png');
    this.link.nativeElement.setAttribute('href', this.canvas.nativeElement.toDataURL("image/png").replace("image/png", "image/octet-stream"));
    this.link.nativeElement.click();
    */
    let signature = new Signature();
    signature.signaturemediatype = "image/jpg";
    this.canvas.nativeElement.toBlob((blob: Blob) => {
      const reader = new FileReader();
      reader.onload = ((e) => {
        signature.signaturedata = window.btoa(<string>(<any>e.target).result);
        let user = this._route.snapshot.paramMap.get("user");
        if(user){
          this._backEndService.post("api/signature/" + user, signature).subscribe(() => {
            this.setPopUp(new PopUpData({
              "message": "Ihre Signatur wurde gespeichert und dem Nutzer mit der ID " + user + " zugeordnet",
              "icon": "done",
              "iconClass": "success"
            }));
            this.clearSignature();
            this._app.tick();
          });
        } else {
          this._backEndService.post("api/account/signature", signature).subscribe(() => {
            this.setPopUp(new PopUpData({
              "message": "Ihre Signatur wurde gespeichert",
              "icon": "done",
              "iconClass": "success"
            }));
            this.clearSignature();
          });
        }
      });
      reader.readAsBinaryString(blob);
    }, signature.signaturemediatype, 1);
  }
  /**
   * the method set's the pop up data
   */
  setPopUp(data: PopUpData) {
    this._popUp.openFromComponent(PopUpComponent, {
      duration: 2000,
      data: data
    });
  }
}
